import axios from "vue-app/utils/axios";
import {
  DTOAgentEngagementTestPhone,
  DTODesignParameters,
  DTOEngagementInfoBox,
  DTOEngagementMessagePreview,
  DTOEngagementReportResponse,
  DTOEngagementRunResponse,
  DTOEngagementSender,
  DTOEngagementSenderResponse,
  DTOEngagementsMetric,
  DTOEngagementsResponse,
  DTOTrackerEngagementSmsAutoCreationQueue,
} from "vue-app/api/dto";
import { CHANNEL_TYPE, ENGAGEMENT_TYPES, ORDER_INDEX } from "vue-app/types/enums";
import _ from "lodash";
import { LocalRejectedProducts } from "vue-app/components/common/CommonReportDrawer/machine/machineCommonReportDrawer.types.ts";
import { extractReasonsWithIds } from "vue-app/components/common/CommonReportDrawer/machine/machineCommonReportDrawer.helpers.ts";

type NameParam = "country" | "city" | "browser_language" | "first_name" | "state";

export async function getListOfAllValuesForColumnAndCountThem(name: NameParam) {
  const response = await axios.post(`/json/engagements/getListOfAllValuesForColumnAndCountThem?name=${name}`);
  return response.data.data;
}

// We were able to generate with ConvertToTypeScript DTO with engagementsMap only.
// The other keys have been added in php. That's why response type is declared below.
export type EngagementsDTO = {
  engagementsMap: DTOEngagementsResponse["engagementsMap"];
  nonTransactionalEngagements: Array<{ [key: string]: DTOEngagementInfoBox & { link_url: string } }>;
  paidFeatures: Array<{
    data_CS_name: string;
    link_url: string;
    engagementType: string;
    engagementStatus: string | null;
    modificationDate: string | null;
    engagementPauseReason: string | null;
    response_id: number;
  }>;
  paidFeaturesStatus: { [key: string]: boolean };
  engagementsMetric: {
    deliveredCount: number;
    prevDeliveredCount: number;
    deliveredCountPercentageChange: number;
    orders: number;
    prevOrders: number;
    ordersPercentageChange: number;
    totalOrdersBasePaymentValue: number;
    prevTotalOrdersBasePaymentValue: number;
    totalOrdersBasePaymentValuePercentageChange: number;
    activeReadyFlows: number;
  };
  hasOrdersWithStatus: boolean;
};

export async function getEngagements() {
  const response = await axios.get<EngagementsDTO>("/json/engagements");
  return response.data;
}

export async function getEngagementsTopMetrics() {
  const response = await axios.get<DTOEngagementsMetric>("/json/engagements/top-metrics");
  return response.data;
}

export async function getEngagementMessagesPreviews(engagementId: number) {
  const response = await axios.get<DTOEngagementMessagePreview[]>("/json/engagements/" + engagementId + "/messages");
  return response.data;
}

export type SendersDTO = {
  senders: DTOEngagementSender[];
};

export async function getSenders(engagementTypeId: number) {
  const { data } = await axios.get<SendersDTO>("/engagements/getSenders", {
    params: { engagement_type_id: engagementTypeId },
  });
  return data;
}

/**
 * We have "v2" because we couldn't generate DTO for the "/engagements/getSenders" endpoint.
 */
export async function getSendersV2(): Promise<DTOEngagementSenderResponse> {
  const { data } = await axios.get<DTOEngagementSenderResponse>("/engagements/v2/getSenders");
  return data;
}

export type UpdateSenderDTO = {
  sender: DTOEngagementSender;
  status: string;
};

export async function addNewSenderWithDomain(trackerId: number, fromName: string, fromEmail: string, replyTo: string) {
  const { data } = await axios.post<UpdateSenderDTO>("/engagements/addNewSenderWithDomain", {
    from_name: fromName,
    from_email: fromEmail,
    reply_to: replyTo,
    tracker_id: trackerId,
  });
  return data;
}

export type DeleteSenderDTO = { status: string };

export async function deleteSender(id: number) {
  const { data } = await axios.delete<DeleteSenderDTO>(`/engagements/sender/${id}`);
  return data;
}

export type Categories = {
  [key: string]: {
    trackerProductCategoryId: number;
    name: string;
    trackerProductImage1: string;
    trackerProductTitle1: string;
    trackerProductUrl1: string;
    trackerProductImage2?: string;
    trackerProductTitle2?: string;
    trackerProductUrl2?: string;
    trackerProductImage3?: string;
    trackerProductTitle3?: string;
    trackerProductUrl3?: string;
    trackerProductImage4?: string;
    trackerProductTitle4?: string;
    trackerProductUrl4?: string;
    trackerProductImage5?: string;
    trackerProductTitle5?: string;
    trackerProductUrl5?: string;
    trackerProductImage6?: string;
    trackerProductTitle6?: string;
    trackerProductUrl6?: string;
  };
};

export type EngagementWithMessagesDTO = DTODesignParameters & {
  messages: Array<{
    orderIndex: number;
    channelType: number;
    language: null;
    groupId: number | null; // it will be null in case of "Introduction messages"
    title: string;
  }>;
};

export async function updateEngagementParameterValue(
  typeId: number,
  parameterValue: number | string,
  isNull: boolean = false
) {
  await axios.get("/engagements/updateEngagementParameterValueEx", {
    params: {
      engagement_parameter_type_id: typeId,
      engagement_parameter_value: parameterValue,
      is_null: isNull,
    },
  });
}

export async function migrateToSingleLanguage(engagementId: number, language: string) {
  await axios.get("/engagements/migrateToSingleLanguage", {
    params: {
      engagement_id: engagementId,
      language: language,
    },
  });
}

export async function updateEngagementMessageParameterValue(
  engagementId: number,
  orderIndex: number,
  typeId: number,
  parameterValue: number | string
) {
  await axios.post("/engagements/updateEngagementMessageParameterValue", {
    engagement_id: engagementId,
    order_index: orderIndex,
    engagement_message_parameter_type_id: typeId,
    engagement_message_parameter_value: parameterValue,
  });
}

export async function updateEngagementMessageGroupParameterValue(
  engagementId: number,
  groupId: number,
  orderIndex: number,
  typeId: number,
  parameterValue: number | string
) {
  await axios.post("/engagements/updateEngagementMessageGroupParameterValue", {
    engagement_id: engagementId,
    order_index: orderIndex,
    engagement_message_group_id: groupId,
    engagement_message_parameter_type_id: typeId,
    engagement_message_parameter_value: parameterValue,
  });
}

type DTOEngagementMessage = {
  engagement_type_id: number;
  html: string;
  editor_layout: string;
  subject: string;
  preheader: string;
  order_index?: number;
  engagement_message_language?: string;
  group_id?: number;
  channel_type_id?: number;
  autosave: boolean;
};

export async function postEngagementsDesignSaveMessage(
  engagementTypeId: number,
  html: string,
  design: string,
  subject: string,
  preheader: string,
  autosave: boolean = false,
  orderIndex?: number,
  groupId?: number,
  messageLanguage?: string,
  editorType?: number
) {
  const payload: DTOEngagementMessage = {
    engagement_type_id: engagementTypeId,
    order_index: orderIndex,
    engagement_message_language: messageLanguage,
    channel_type_id: editorType,
    html,
    editor_layout: design,
    subject,
    preheader,
    autosave,
  };

  if (groupId) {
    payload.group_id = groupId;
  }

  await axios.post("/engagements/design/saveMessage", payload);
}

export async function getEngagementMessagePreview(engagementMessageId: number) {
  const { data } = await axios.get(`/engagements/engagement-message/${engagementMessageId}/preview`);
  return data;
}

export async function getEngagementMessageShortenLinks(
  engagementMessageId: number
): Promise<{ shortenLinks: boolean }> {
  const { data } = await axios.get(`/engagements/engagement-message/${engagementMessageId}/shorten-links`);
  return data;
}

export async function saveEngagementMessageDesign(
  html: string,
  design: string,
  engagementMessageId: number,
  autosave: boolean = false
) {
  await axios.post("/engagements/save-engagement-message-design", {
    html: html,
    editor_layout: design,
    engagement_message_id: engagementMessageId,
    autosave: autosave,
  });
}

export interface EngagementTagCustomerSettings {
  engagementTagCustomerSettingsId: number;
  trackerId: number;
  engagementId: number;
  engagementMessageId: number;
  creationDate: string;
  modificationDate: string;
  sendTagId: number | null;
  openTagId: number | null;
  clickTagId: number | null;
}

export async function getEngagementTagCustomerSettings(
  engagementId: number,
  engagementMessageId: number
): Promise<EngagementTagCustomerSettings> {
  const { data } = await axios.get(
    `/engagements/${engagementId}/engagement-message/${engagementMessageId}/tag-customer-settings`
  );
  return data;
}

export async function saveEngagementTagCustomerSettings(
  engagementId: number,
  engagementMessageId: number,
  openTagId: number | null,
  clickTagId: number | null
): Promise<void> {
  await axios.post(`/engagements/${engagementId}/engagement-message/${engagementMessageId}/tag-customer-settings`, {
    open_tag_id: openTagId,
    click_tag_id: clickTagId,
  });
}

export async function saveSubjectAndPreheader(engagementMessageId: number, subject: string, preheader: string) {
  await axios.post("/engagements/save-subject-and-preheader", {
    preheader: preheader,
    subject: subject,
    engagement_message_id: engagementMessageId,
  });
}

export interface EngagementMessageFromLoadMessageDTO {
  engagementMessageChannelType: number;
  engagementMessageLanguage: string | null;
  engagementMessageOrderIndex: number;
  engagementMessageGroupId: number;
  html: string;
  editorLayout: string;
  subject: string;
  preheader: string;
  utmSource: string;
  utmMedium: string;
  utmTerm: string;
  utmCampaign: string;
  areGaTagsOverwritten: boolean;
  channelType: string | null;
  status: string;
  engagementMessageId: number;
  engagementMessageElementParameters: any;
  couponPoolId: number;
}

export interface DTOEngagementRun {
  runResponse: DTOEngagementRunResponse;
  status: string;
}

export async function getEngagementsDesignLoadMessage(
  engagementTypeId: keyof typeof ENGAGEMENT_TYPES,
  orderIndex: ORDER_INDEX,
  groupId: number | null
) {
  const { data } = await axios.get<EngagementMessageFromLoadMessageDTO>("/engagements/design/loadMessage", {
    params: {
      engagement_type_id: ENGAGEMENT_TYPES[engagementTypeId],
      order_index: orderIndex,
      group_id: groupId,
      engagement_message_language: null,
      send_channel: CHANNEL_TYPE.EMAIL,
    },
  });
  return data;
}

export async function updateUtmSettings(
  utmCampaign: string,
  utmSource: string,
  utmMedium: string,
  utmTerm: string,
  areOverwritten: boolean,
  engagementTypeId: number,
  engagementMessageId: number
) {
  await axios.post("/engagements/updateGaTags", {
    utm_campaign: utmCampaign,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_term: utmTerm,
    are_overwritten: areOverwritten.toString(),
    engagement_type_id: engagementTypeId,
    engagement_message_id: engagementMessageId,
  });
}

export async function updateUrlToRedirect(engagementMessageId: number, value: string) {
  await axios.post("/engagements/updateUrlToRedirect", {
    engagement_message_id: engagementMessageId,
    value,
  });
}

export async function postEngagementRun(engagementTypeId: number) {
  const { data } = await axios.post<DTOEngagementRun>("/engagements/run", {
    engagement_type_id: engagementTypeId,
  });
  if (!data.runResponse.runSuccessful) {
    throw new Error(JSON.stringify(data.runResponse));
  }
  return data;
}

export async function postEngagementPause(engagementTypeId: number, reason?: string, otherText?: string) {
  await axios.post<DTOEngagementRun>("/engagements/pause", {
    engagement_type_id: engagementTypeId,
    pause_reason: reason,
    pause_reason_text: otherText,
  });
}

export async function getEngagementIsMultiLanguage(engagementTypeId: number) {
  const { data } = await axios.get("/engagements/getIsMultiLanguage?engagement_type_id=" + engagementTypeId);
  return data;
}

export async function getEngagementStatistics(
  startDate: string,
  endDate: string,
  engagementIds: string,
  engagementMessageId?: string
) {
  const { data } = await axios.get<{ data: { [key: string]: DTOEngagementReportResponse } }>(
    "/json/engagements/getEngagementStatistics",
    {
      params: {
        start_date: startDate,
        end_date: endDate,
        engagement_ids: engagementIds,
        engagement_message_id: engagementMessageId,
      },
    }
  );
  return data;
}

export type LoadAttributionsDTO = {
  attributions: Array<{
    customerId: number;
    firstName: string;
    lastName: string;
    avatar: string;
    isDeleted: boolean;
    basePaymentValue: number;
    orderDate: number;
    trackerProductItems: Array<{
      trackerProductId: number;
      trackerId: number;
      productId: string;
      productTitle: string;
      productImage: string;
      productSku: string;
      creationDate: number;
      productImageProxy: string;
      productImageProxyStatus: number;
      productImageResizedProxy: string;
      dataSourceId: number;
      productUrl: string;
      modificationDate: number;
      height: number;
      width: number;
      productImageProxyMd5: string;
      productImageProxyByteSize: number;
      productImageModificationDate: number;
      productPrice: number;
      productCurrency: string;
      productImageProxyStatusWebp: number;
      productSalePrice: number;
      productSalePriceEffectiveStartDate: number;
      productSalePriceEffectiveEndDate: number;
      preorderedProductShippDate: number;
      productExpirationDate: number;
      description: string;
      source: number;
      genderTypeId: number;
      availabilityModificationDate?: number;
      isAvailable: boolean;
      cachedAt: number;
      updatedByNewFeedVersion: boolean;
      image: string;
      productBasePrice: number;
      productDiscountedPrice: number;
      actualPriceAsBigDecimal: number;
      imageResized: string;
      available: boolean;
      discountedPriceIfIsInRangeOfSalePriceEffectiveDate: number;
      actualPrice: number;
    }>;
    trackerProductsTotalCount: number;
    attributionReasons: string[];
  }>;
  engagement: string | string[] | null;
};

export async function loadAttributions(
  engagementId: number,
  page: number,
  minValue: number,
  maxValue: number,
  attributionReasonId: number,
  channelTypeId?: CHANNEL_TYPE,
  firstDate?: string,
  lastDate?: string
) {
  const { data } = await axios.post<LoadAttributionsDTO>("/json/engagements/loadAttributions", {
    engagement_id: engagementId,
    page: page,
    min_value: minValue,
    max_value: maxValue,
    attribution_reason_id: attributionReasonId,
    channel_type_id: channelTypeId,
    first_date: firstDate,
    last_date: lastDate,
  });
  return data.attributions;
}

export async function getTrackerProductStats(period: [string, string], engagementId: number) {
  const params: Record<string, any> = {};
  params.engagement_id = engagementId;
  params.start_date = period[0];
  params.end_date = period[1];

  const response = await axios.get("/engagements/getTrackerProductStats", {
    params,
  });
  const productsRejected = response.data?.data;

  const feedUrls = productsRejected.feedUrls;
  const reasonsCount = Object.entries(productsRejected?.reasonsCount).map(([label, value]) => ({ label, value })) || [];
  const trackerProducts = _.uniqBy(productsRejected?.trackerProducts, "trackerProductId") || [];
  const reasonsWithTrackerProductIds = extractReasonsWithIds(productsRejected?.last20 || []);

  return {
    feedUrls,
    reasonsCount,
    trackerProducts,
    reasonsWithTrackerProductIds,
  } as LocalRejectedProducts;
}

export async function getEngagementStatisticTypes() {
  const { data } = await axios.get("/json/engagements/getEngagementStatisticTypes");
  return data.data;
}

export async function postEngagementDesignSwitchToNewDnd(engagementTypeId: number) {
  await axios.post("/engagements/design/switchToNewDnd", {
    engagement_type_id: engagementTypeId,
  });
}

export async function getSendTestEmails() {
  const { data } = await axios.get("/engagements/test-emails");
  return data;
}

export async function addSendTestEmail(email: string) {
  await axios.put("/engagements/test-emails", {
    email,
  });
}

export async function removeSendTestEmail(id: number) {
  await axios.delete("/engagements/test-emails/" + id);
}

export async function getEngagementsTestPhones() {
  const { data } = await axios.get<DTOAgentEngagementTestPhone[]>("/engagements/test-phones");
  return data;
}

export async function addSendTestPhone(phone: string) {
  await axios.put("/engagements/test-phones", {
    phone,
  });
}

export async function removeSendTestPhone(id: number) {
  await axios.delete("/engagements/test-phones/" + id);
}

export async function getScheduledEngagementSmsAutoCreations() {
  const { data } = await axios.get<DTOTrackerEngagementSmsAutoCreationQueue[]>(
    "/engagements/scheduledEngagementSmsAutoCreations"
  );
  return data;
}

export async function deleteScheduledEngagementSmsAutoCreation() {
  const { data } = await axios.delete(`/engagements/scheduledEngagementSmsAutoCreations`);
  return data;
}

export async function postScheduledEngagementSmsAutoCreation() {
  const { data } = await axios.post(`/engagements/scheduledEngagementSmsAutoCreations`);
  return data;
}

export async function getScheduledEngagementWhatsappAutoCreations() {
  const { data } = await axios.get<DTOTrackerEngagementSmsAutoCreationQueue[]>(
    "/engagements/scheduledEngagementWhatsappAutoCreations"
  );
  return data;
}

export async function deleteScheduledEngagementWhatsappAutoCreation() {
  const { data } = await axios.delete(`/engagements/scheduledEngagementWhatsappAutoCreations`);
  return data;
}

export async function postScheduledEngagementWhatsappAutoCreation() {
  const { data } = await axios.post(`/engagements/scheduledEngagementWhatsappAutoCreations`);
  return data;
}

export const sendToHistoricalEligibleClients = async (engagementId: number) => {
  await axios.post("/send-to-historical-eligible-clients", {
    engagement_id: engagementId,
  });
};

export const sendToHistoricalEligibleClientsByType = async (engagementTypeId: number) => {
  await axios.post("/send-to-historical-eligible-clients-by-type", {
    engagement_type_id: engagementTypeId,
  });
};
